/**
 * Pure helpers for the YouTube "Organic vs Paid" card.
 *
 * Deliberately free of React, redux and network imports so it can be unit
 * tested without mocking the store that `api_handlers/index.js` pulls in at
 * module scope. Every function returns new values; nothing is mutated.
 */

const SPLIT_METRICS = [
  'views',
  'impressions',
  'engagement',
  'watch_time',
  'spend',
  'conversions',
];

/** Coerce anything into a finite number, defaulting to 0. */
export function safeNumber(value) {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Divide, returning null rather than Infinity/NaN when the denominator is empty. */
export function safeDivide(numerator, denominator) {
  const bottom = safeNumber(denominator);
  if (bottom === 0) return null;
  return safeNumber(numerator) / bottom;
}

/** Cost per thousand impressions. */
export function costPerMille(spend, impressions) {
  const perImpression = safeDivide(spend, impressions);
  return perImpression === null ? null : perImpression * 1000;
}

/** Cost per view. */
export function costPerView(spend, views) {
  return safeDivide(spend, views);
}

/** Engagement as a percentage of views. */
export function engagementRate(engagement, views) {
  const ratio = safeDivide(engagement, views);
  return ratio === null ? null : ratio * 100;
}

/** Organic share of the organic+paid total, as a percentage. */
export function organicSharePct(organic, paid) {
  const total = safeNumber(organic) + safeNumber(paid);
  if (total === 0) return null;
  return (safeNumber(organic) / total) * 100;
}

/** Accept either a raw response body or an axios-style `{ data }` wrapper. */
export function unwrapResponse(response) {
  if (!response || typeof response !== 'object') return {};
  if (response.data && typeof response.data === 'object') return response.data;
  return response;
}

/** Sum every organic_ and paid_ split metric across a list of creators. */
export function rollUpCreators(creators) {
  const list = Array.isArray(creators) ? creators : [];
  return list.reduce((totals, creator) => {
    const next = { ...totals };
    SPLIT_METRICS.forEach((metric) => {
      next[`organic_${metric}`] =
        safeNumber(next[`organic_${metric}`]) + safeNumber(creator[`organic_${metric}`]);
      next[`paid_${metric}`] =
        safeNumber(next[`paid_${metric}`]) + safeNumber(creator[`paid_${metric}`]);
    });
    return next;
  }, emptyTotals());
}

function emptyTotals() {
  return SPLIT_METRICS.reduce((acc, metric) => {
    return { ...acc, [`organic_${metric}`]: 0, [`paid_${metric}`]: 0 };
  }, {});
}

function volumeRow(key, label, source) {
  const organic = safeNumber(source[`organic_${key}`]);
  const paid = safeNumber(source[`paid_${key}`]);
  return { key, label, organic, paid, total: organic + paid, format: 'count' };
}

/**
 * One row per metric, always in `organic, paid, total` order so the rendered
 * columns cannot drift from the table header.
 */
export function buildOverallRows(overall) {
  const source = overall && typeof overall === 'object' ? overall : {};

  const impressions = volumeRow('impressions', 'Impressions', source);
  const views = volumeRow('views', 'Views', source);
  const engagement = volumeRow('engagement', 'Engagement', source);
  const watchTime = volumeRow('watch_time', 'Watch Time (mins)', source);
  const conversions = volumeRow('conversions', 'Conversions', source);

  const organicSpend = safeNumber(source.organic_spend);
  const paidSpend = safeNumber(source.paid_spend);

  return [
    impressions,
    views,
    {
      key: 'cpm',
      label: 'CPM',
      organic: costPerMille(organicSpend, impressions.organic),
      paid: costPerMille(paidSpend, impressions.paid),
      total: costPerMille(organicSpend + paidSpend, impressions.total),
      format: 'currency',
    },
    {
      key: 'cpv',
      label: 'CPV',
      organic: costPerView(organicSpend, views.organic),
      paid: costPerView(paidSpend, views.paid),
      total: costPerView(organicSpend + paidSpend, views.total),
      format: 'currency',
    },
    {
      key: 'engagement_rate',
      label: 'Engagement Rate',
      organic: engagementRate(engagement.organic, views.organic),
      paid: engagementRate(engagement.paid, views.paid),
      total: engagementRate(engagement.total, views.total),
      format: 'percent',
    },
    engagement,
    watchTime,
    conversions,
  ];
}

/** One shaped row per creator, sorted by total views descending. */
export function buildCreatorRows(creators) {
  const list = Array.isArray(creators) ? creators : [];

  return list
    .map((creator) => {
      const organicViews = safeNumber(creator.organic_views);
      const paidViews = safeNumber(creator.paid_views);
      const organicImpressions = safeNumber(creator.organic_impressions);
      const paidImpressions = safeNumber(creator.paid_impressions);
      const organicEngagement = safeNumber(creator.organic_engagement);
      const paidEngagement = safeNumber(creator.paid_engagement);
      const paidSpend = safeNumber(creator.paid_spend);

      return {
        influencerId: creator.influencer_id ?? null,
        name: creator.influencer_name || 'Unknown creator',
        avatar: creator.avatar || null,
        videoCount: safeNumber(creator.video_count),
        organicViews,
        paidViews,
        totalViews: organicViews + paidViews,
        organicImpressions,
        paidImpressions,
        totalImpressions: organicImpressions + paidImpressions,
        organicSharePct: organicSharePct(organicViews, paidViews),
        engagementRate: engagementRate(
          organicEngagement + paidEngagement,
          organicViews + paidViews,
        ),
        paidSpend,
        paidCpv: costPerView(paidSpend, paidViews),
        paidCpm: costPerMille(paidSpend, paidImpressions),
      };
    })
    .sort((a, b) => b.totalViews - a.totalViews);
}

const COUNT_UNITS = [
  { limit: 1e9, suffix: 'B' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'K' },
];

function formatCount(value) {
  const abs = Math.abs(value);
  const unit = COUNT_UNITS.find((candidate) => abs >= candidate.limit);
  if (!unit) return String(Math.round(value));
  const scaled = value / unit.limit;
  return `${parseFloat(scaled.toFixed(1))}${unit.suffix}`;
}

/**
 * Render a cell value for display. `null` means "not computable" (for example
 * a CPM with no impressions) and renders as a dash. Zero is a real value and
 * renders as zero.
 */
export function formatCell(value, format) {
  if (value === null || value === undefined) return '-';
  const numeric = safeNumber(value);

  if (format === 'currency') return numeric.toFixed(2);
  if (format === 'percent') return `${numeric.toFixed(2)}%`;
  return formatCount(numeric);
}
